import {
  ArrowUturnLeftIcon,
  CameraIcon,
  ChatBubbleLeftIcon,
  ShareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import React, { useEffect, useRef, useState } from 'react'
import apiService from '../api/apiService'
import { useAuth } from '../contexts/AuthContext'
import { uploadImageToS3 } from '../utils/imageUpload'
import { Button } from './ui'

const RecipeComments = ({ recipeId, recipeName, highlightedCommentId }) => {
  const { user } = useAuth()
  const [dbUser, setDbUser] = useState(null)
  const [comments, setComments] = useState([])
  const [totalComments, setTotalComments] = useState(0)
  const [loading, setLoading] = useState(true)
  const [commentText, setCommentText] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showCommentForm, setShowCommentForm] = useState(false)
  const [replyingTo, setReplyingTo] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [deletingComments, setDeletingComments] = useState(new Set())
  const fileInputRef = useRef(null)

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return

      try {
        const userData = await apiService.getUserByEmail(user.email)
        setDbUser(userData)
      } catch (err) {
        console.error('Error fetching user data:', err)
      }
    }

    fetchUserData()
  }, [user])

  // Fetch comments
  useEffect(() => {
    const fetchComments = async () => {
      if (!recipeId) return

      try {
        setLoading(true)

        // API call to fetch comments
        const response = await fetch(`/api/recipes/${recipeId}/comments`)

        if (!response.ok) {
          throw new Error('Failed to fetch comments')
        }

        const data = await response.json()
        setComments(data.comments || [])
        setTotalComments(data.count || 0)
      } catch (err) {
        console.error('Error fetching comments:', err)
        setError('Failed to load comments')
      } finally {
        setLoading(false)
      }
    }

    fetchComments()
  }, [recipeId])

  // Scroll to highlighted comment after comments are loaded
  useEffect(() => {
    if (highlightedCommentId && comments.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        const commentElement = document.getElementById(
          `comment-${highlightedCommentId}`
        )
        if (commentElement) {
          commentElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
          // Add a subtle highlight animation
          commentElement.style.boxShadow = '0 0 15px rgba(34, 197, 94, 0.3)'
          setTimeout(() => {
            if (commentElement) {
              commentElement.style.boxShadow = ''
            }
          }, 3000)
        }
      }, 500)
    }
  }, [highlightedCommentId, comments])

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (
      !file.type.startsWith('image/') &&
      !file.name.toLowerCase().endsWith('.heic') &&
      !file.name.toLowerCase().endsWith('.heif')
    ) {
      setError('Please select an image file.')
      return
    }

    // Validate file size (10MB limit)
    const MAX_SIZE = 10 * 1024 * 1024
    if (file.size > MAX_SIZE) {
      setError('Image size must be less than 10MB.')
      return
    }

    try {
      setError('')
      setUploading(true)

      const imageUrlResponse = await uploadImageToS3(file)
      setImageUrl(imageUrlResponse)

      // Reset file input
      if (event.target) {
        event.target.value = ''
      }
    } catch (error) {
      console.error('Upload error:', error)
      setError(error.message || 'Failed to upload image')
    } finally {
      setUploading(false)
    }
  }

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleSubmitComment = async (e) => {
    e.preventDefault()

    if (!dbUser) {
      setError('You must be logged in to comment')
      return
    }

    if (!commentText.trim()) {
      setError('Please enter a comment')
      return
    }

    try {
      setIsSubmitting(true)

      const response = await fetch(`/api/recipes/${recipeId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          comment_text: commentText,
          image_url: imageUrl,
          user_id: dbUser.id,
          parent_comment_id: replyingTo?.id || null,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to post comment')
      }

      const newComment = await response.json()

      // Optimistic UI update
      if (replyingTo) {
        // If it's a reply, add it to the parent comment's replies
        setComments((prevComments) =>
          prevComments.map((comment) =>
            comment.id === replyingTo.id
              ? { ...comment, replies: [...comment.replies, newComment] }
              : comment
          )
        )
      } else {
        // If it's a new top-level comment, add it to the list
        setComments((prevComments) => [newComment, ...prevComments])
      }

      // Reset form
      setCommentText('')
      setImageUrl('')
      setShowCommentForm(false)
      setReplyingTo(null)
      setTotalComments((prev) => prev + 1)
    } catch (err) {
      console.error('Error posting comment:', err)
      setError('Failed to post comment')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReply = (comment) => {
    setReplyingTo(comment)
    setShowCommentForm(true)
    setCommentText('')
    setImageUrl('')
  }

  const handleShare = (comment) => {
    // Create a proper shareable URL that includes the comment ID
    const baseUrl = `${window.location.origin}/recipe/${recipeId}`
    const shareUrl = `${baseUrl}?comment=${comment.id}`

    if (navigator.share) {
      navigator
        .share({
          title: `Comment on ${recipeName}`,
          text: `Check out this comment on ${recipeName}: "${comment.comment_text}"`,
          url: shareUrl,
        })
        .catch((err) => console.error('Error sharing:', err))
    } else {
      // Fallback for browsers that don't support navigator.share
      const shareText = `Check out this comment on ${recipeName}: "${comment.comment_text}" ${shareUrl}`
      navigator.clipboard
        .writeText(shareText)
        .then(() => alert('Link copied to clipboard!'))
        .catch((err) => console.error('Failed to copy:', err))
    }
  }

  const handleDeleteComment = async (comment) => {
    if (!dbUser) {
      setError('You must be logged in to delete comments')
      return
    }

    if (comment.user_id !== dbUser.id) {
      setError('You can only delete your own comments')
      return
    }

    if (
      !window.confirm(
        'Are you sure you want to delete this comment? This action cannot be undone.'
      )
    ) {
      return
    }

    try {
      setDeletingComments((prev) => new Set(prev).add(comment.id))

      const response = await fetch(
        `/api/recipes/${recipeId}/comments/${comment.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: dbUser.id,
          }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to delete comment')
      }

      const result = await response.json()

      // Optimistic UI update
      if (result.isReply) {
        // If it's a reply, remove it from the parent comment's replies
        setComments((prevComments) =>
          prevComments.map((parentComment) => ({
            ...parentComment,
            replies: parentComment.replies.filter(
              (reply) => reply.id !== comment.id
            ),
          }))
        )
      } else {
        // If it's a top-level comment, remove it entirely (including all replies)
        setComments((prevComments) =>
          prevComments.filter((c) => c.id !== comment.id)
        )
      }

      // Update total count
      setTotalComments((prev) => Math.max(0, prev - 1))
    } catch (err) {
      console.error('Error deleting comment:', err)
      setError(err.message || 'Failed to delete comment')
    } finally {
      setDeletingComments((prev) => {
        const newSet = new Set(prev)
        newSet.delete(comment.id)
        return newSet
      })
    }
  }

  // Render comment with user info
  const renderComment = (comment, isReply = false) => {
    const { user: commentUser } = comment
    const isHighlighted =
      highlightedCommentId && comment.id.toString() === highlightedCommentId
    const isAuthor = dbUser && comment.user_id === dbUser.id
    const isDeleting = deletingComments.has(comment.id)

    return (
      <div
        key={comment.id}
        id={`comment-${comment.id}`}
        className={`p-4 border rounded-lg mb-4 transition-all duration-300 ${
          isReply
            ? isHighlighted
              ? 'ml-8 border-green-300 bg-green-50'
              : 'ml-8 border-gray-200 bg-gray-50'
            : isHighlighted
              ? 'border-green-300 bg-green-50'
              : 'border-gray-300'
        } ${isDeleting ? 'opacity-50' : ''}`}
      >
        <div className="flex items-start space-x-3">
          {/* User avatar */}
          <div className="flex-shrink-0">
            {commentUser?.image_url ? (
              <img
                src={commentUser.image_url}
                alt={`${commentUser.first_name} ${commentUser.last_name}`}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-gray-500 font-medium">
                  {commentUser?.first_name?.[0] || '?'}
                </span>
              </div>
            )}
          </div>

          {/* Comment content */}
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-center mb-1">
              <h4 className="text-sm font-medium text-gray-900">
                {commentUser?.first_name} {commentUser?.last_name}
              </h4>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500">
                  {new Date(comment.created_at).toLocaleDateString()}
                </span>
                {isAuthor && !isDeleting && (
                  <button
                    onClick={() => handleDeleteComment(comment)}
                    className="text-xs text-red-500 hover:text-red-700 p-1"
                    title="Delete comment"
                  >
                    <TrashIcon className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            <p className="text-sm text-gray-700 mb-2">{comment.comment_text}</p>

            {/* Comment image if exists */}
            {comment.image_url && (
              <div className="mt-2 mb-3">
                <img
                  src={comment.image_url}
                  alt="User's recipe creation"
                  className="rounded-lg max-h-64 object-cover"
                />
              </div>
            )}

            {/* Action buttons */}
            <div className="flex space-x-4 mt-2">
              <button
                onClick={() => handleReply(comment)}
                disabled={isDeleting}
                className="flex items-center text-xs text-gray-500 hover:text-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChatBubbleLeftIcon className="w-4 h-4 mr-1" />
                <span>Reply</span>
              </button>

              <button
                onClick={() => handleShare(comment)}
                disabled={isDeleting}
                className="flex items-center text-xs text-gray-500 hover:text-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ShareIcon className="w-4 h-4 mr-1" />
                <span>Share</span>
              </button>
            </div>
          </div>
        </div>

        {/* Replies */}
        {!isReply && comment.replies && comment.replies.length > 0 && (
          <div className="mt-3">
            {comment.replies.map((reply) => renderComment(reply, true))}
          </div>
        )}
      </div>
    )
  }

  // Render photo gallery preview of user submissions
  const renderPhotoGallery = () => {
    const commentsWithImages = comments.filter((comment) => comment.image_url)

    if (commentsWithImages.length === 0) {
      return null
    }

    return (
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-3">User Creations</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {commentsWithImages.slice(0, 8).map((comment) => {
            const isAuthor = dbUser && comment.user_id === dbUser.id
            const isDeleting = deletingComments.has(comment.id)

            return (
              <div key={comment.id} className="relative group">
                <img
                  src={comment.image_url}
                  alt={`${comment.user.first_name}'s creation`}
                  className={`aspect-square object-cover rounded-lg ${isDeleting ? 'opacity-50' : ''}`}
                />
                {isAuthor && !isDeleting && (
                  <button
                    onClick={() => handleDeleteComment(comment)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    title="Delete creation"
                  >
                    <TrashIcon className="w-3 h-3" />
                  </button>
                )}
                {isDeleting && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="mt-8 mb-12">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">
          Community Creations{' '}
          <span className="text-lg font-normal text-gray-500">
            ({totalComments})
          </span>
        </h2>
      </div>

      {/* Photo Gallery */}
      {renderPhotoGallery()}

      {/* Comment Form */}
      {!showCommentForm ? (
        <div className="mb-6">
          <Button
            onClick={() => setShowCommentForm(true)}
            className="flex items-center justify-center w-full py-3"
          >
            <CameraIcon className="w-5 h-5 mr-2" />
            {replyingTo ? 'Reply to Comment' : 'Share Your Creation!'}
          </Button>
        </div>
      ) : (
        <div className="bg-gray-50 p-4 rounded-lg mb-6 border border-gray-200">
          {replyingTo && (
            <div className="mb-3 flex items-center text-sm text-gray-600">
              <ArrowUturnLeftIcon className="w-4 h-4 mr-2" />
              Replying to {replyingTo.user.first_name}&apos;s comment
              <button
                onClick={() => setReplyingTo(null)}
                className="ml-2 text-red-500 text-xs"
              >
                Cancel
              </button>
            </div>
          )}

          <form onSubmit={handleSubmitComment}>
            {error && (
              <div className="bg-red-50 text-red-600 p-2 rounded mb-3 text-sm">
                {error}
              </div>
            )}

            {/* Image Preview */}
            {imageUrl && (
              <div className="mb-3 relative">
                <img
                  src={imageUrl}
                  alt="Preview"
                  className="max-h-48 rounded-lg object-cover"
                />
                <button
                  type="button"
                  onClick={() => setImageUrl('')}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            )}

            <div className="flex space-x-2 mb-3">
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                onChange={handleImageUpload}
              />

              <button
                type="button"
                onClick={triggerFileInput}
                disabled={uploading}
                className="flex-shrink-0 bg-white border border-gray-300 rounded-md py-2 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {uploading ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Uploading...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <CameraIcon className="w-4 h-4 mr-2" />
                    {imageUrl ? 'Change Photo' : 'Add Photo'}
                  </span>
                )}
              </button>
            </div>

            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder={
                replyingTo
                  ? 'Write your reply...'
                  : 'Share your experience with this recipe...'
              }
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="3"
              required
            />

            <div className="flex justify-end space-x-2 mt-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowCommentForm(false)
                  setReplyingTo(null)
                  setCommentText('')
                  setImageUrl('')
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !commentText.trim()}
              >
                {isSubmitting
                  ? 'Posting...'
                  : replyingTo
                    ? 'Post Reply'
                    : 'Post Comment'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Comments List */}
      {loading ? (
        <div className="flex justify-center my-8">
          <div className="animate-pulse space-y-4 w-full">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-100 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-full mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <ChatBubbleLeftIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p>Be the first to share your experience with this recipe!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {comments.map((comment) => renderComment(comment))}
        </div>
      )}
    </div>
  )
}

export default RecipeComments
